import type { ReactNode } from 'react'
import { Dialog } from 'primereact/dialog'
import Button from './Button'

/**
 * Paylaşılan form Dialog kabuğu (anayasa: overlay PrimeReact Dialog styled + Reuse Mandate). Tutarlı
 * Header (başlık + kapat) / Body (children) / Footer (<b>yalnız</b> birincil aksiyon). Responsive: içeriğe
 * göre genişlik, ≤768px'te neredeyse tam genişlik. Tüm ekranlar bunu yeniden kullanır.
 *
 * <p><b>İptal butonu YOKTUR</b> (kullanıcı kararı 2026-08-28, uygulama geneli): vazgeçme yalnız
 * başlıktaki <b>×</b> ile yapılır. Maske tıklaması ({@code dismissableMask}) ve Esc de kapatmaya devam
 * eder, yani klavyeyle çıkış yolu kaybolmaz — kalkan tek şey ikinci bir <i>buton</i>dur. Aynı karar önce
 * yalnız salt-okuma hâli için {@code hideFooter} ile uygulanmıştı; sonra tüm pop-up'lara yayıldı.
 */
export interface FormDialogProps {
  visible: boolean
  header: string
  /** Başlığın yanında gösterilen ikincil bağlam (ör. rol atanan kullanıcının adı). Boşsa yalnız başlık. */
  headerContext?: ReactNode
  submitLabel: string
  submitting?: boolean
  /** Birincil aksiyon devre dışı (ör. geçersiz form). */
  submitDisabled?: boolean
  /**
   * Footer'ı (birincil aksiyon) <b>hiç çizme</b> — pop-up salt-okuma açıldığında.
   *
   * <p>`submitDisabled`ten farkı: o, butonu <b>gösterip</b> pasifleştirir ("şu an yapılamaz");
   * bu ise kullanıcının o işlemi <b>hiç yapamayacağı</b> hâl içindir (yetki yok) — kullanıcı isteği
   * 2026-08-28: <i>"butonu hiç görmek istemediler, çarpı işaretini görmesi yeterli"</i>. Pop-up
   * yalnızca başlıktaki <b>×</b> ile kapanır (maske tıklaması ve Esc de çalışmaya devam eder).
   *
   * <p>Enter ile gönderim de kapanır: footer yoksa gönderilecek bir aksiyon da yoktur.
   */
  hideFooter?: boolean
  /**
   * <b>Salt okunur görüntüleme</b> (126, 26.10.2026 — Kısıtlı Yetkili asistan): footer çizilmez
   * (`hideFooter` gibi) ve gövde bir `<fieldset className="ui-form-readonly">` içine alınır.
   * İçeriği yalnız pop-up'ta görünen kayıtlar (ör. şablon gövdesi) bu kiple açılır. Güvenlik sınırı
   * DEĞİLDİR — yazmayı sunucu reddeder; amaç kullanıcıya çalışmayacak bir eylem sunmamaktır.
   *
   * <p><b>Fieldset `disabled` DEĞİLDİR ve bu bilinçlidir</b> (THY AI review HIGH bulgusu,
   * 26.10.2026): `disabled` içerideki `input`/`textarea`'yı klavye odak sırasından tamamen
   * çıkarıyor, yani kutuya sığmayan metin klavye-only kullanıcı tarafından okunamıyordu.
   * <b>Her alan kendi `readOnly`'sini taşır</b> — çağıran dialogun sorumluluğudur; bu prop tek
   * başına hiçbir denetimi pasifleştirmez. Yeni bir salt-okunur dialog eklerken alanlarına
   * `readOnly` geçirmeyi unutmayın; koruma `AdminReadOnly*` testlerindedir.
   */
  readOnly?: boolean
  onSubmit: () => void
  onHide: () => void
  children: ReactNode
  /** Dialog genişliği (varsayılan 32rem). Geniş içerik (ör. PickList) için büyütülür. */
  width?: string
}

export default function FormDialog({
  visible,
  header,
  headerContext,
  submitLabel,
  submitting = false,
  submitDisabled = false,
  hideFooter = false,
  readOnly = false,
  onSubmit,
  onHide,
  children,
  width = '32rem',
}: Readonly<FormDialogProps>) {
  const noFooter = hideFooter || readOnly
  const footer = noFooter ? undefined : (
    <div className="ui-dialog-footer">
      <Button
        label={submitLabel}
        onClick={onSubmit}
        loading={submitting}
        disabled={submitDisabled}
      />
    </div>
  )
  return (
    <Dialog
      visible={visible}
      header={
        // Ayırıcı boşluk JSX'te (yalnız CSS margin olsa erişilebilir ad bitişik okunurdu).
        headerContext ? (
          <>
            {header} <span className="ui-dialog-header-context">{headerContext}</span>
          </>
        ) : (
          header
        )
      }
      footer={footer}
      onHide={onHide}
      modal
      // PrimeReact `blockScroll` varsayılanı `false`tur — maske görünse de arka plan tekerlek/dokunmayla
      // kayardı (kullanıcı bildirimi). Modal açıkken gövde kaydırması tamamen kilitlenir.
      blockScroll
      dismissableMask
      draggable={false}
      style={{ width, maxWidth: '92vw' }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!noFooter && !submitDisabled && !submitting) onSubmit()
        }}
      >
        {readOnly ? (
          /* `disabled` KALDIRILDI (THY AI review HIGH, 26.10.2026): içerideki `input`/`textarea`
             kontrollerini klavye odak sırasından çıkarıyordu, yani kutuya sığmayan metin
             klavye-only kullanıcı tarafından odaklanıp kaydırılarak okunamıyordu. Artık her
             alan KENDİ `readOnly`'siyle geliyor — odaklanılabilir, seçilebilir, değiştirilemez.
             Yazma yasağının yapısal garantisi buradan kalktığı için her salt-okunur dialogun
             alanları tek tek geçilmiştir; koruma `AdminReadOnly*` testlerindedir. */
          <fieldset className="ui-form-readonly">
            {children}
          </fieldset>
        ) : (
          children
        )}
        {/* Enter ile gönderim için gizli submit */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  )
}
